import React from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import "@/App.css";
import "@/index.css";

import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { CompareProvider } from "@/context/CompareContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import FloatingCall from "@/components/FloatingCall";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import OrderTracking from "@/pages/OrderTracking";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import Wishlist from "@/pages/Wishlist";
import Compare from "@/pages/Compare";
import AboutUs from "@/pages/AboutUs";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import ScrollToTop from "@/components/ScrollToTop";
import IntegrationsScripts from "@/components/IntegrationsScripts";
import UsageTracker from "@/components/UsageTracker";
import AdminTraffic from "@/pages/admin/AdminTraffic";

function PublicLayout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) return <Outlet />;
  return (
    <>
      <Header />
      <main className="min-h-[60vh]">
        <Outlet />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <FloatingCall />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <SiteSettingsProvider>
              <WishlistProvider>
                <CompareProvider>
                  <CartProvider>
                  <ScrollToTop />
                  <IntegrationsScripts />
                  <UsageTracker />
                  <Toaster position="top-right" richColors />
                  <Routes>
                  <Route element={<PublicLayout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/product/:slug" element={<ProductDetail />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/wishlist" element={<Wishlist />} />
                    <Route path="/compare" element={<Compare />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/order-success/:orderId" element={<OrderSuccess />} />
                    <Route path="/track/:orderId" element={<OrderTracking />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route
                      path="/account"
                      element={
                        <ProtectedRoute>
                          <Account />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute adminOnly>
                          <AdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route path="products" element={<AdminProducts />} />
                      <Route
                        path="orders"
                        element={
                          <ProtectedRoute fullAdminOnly>
                            <AdminOrders />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="customers" element={<AdminCustomers />} />
                      <Route path="traffic" element={<AdminTraffic />} />
                      <Route path="coupons" element={<AdminCoupons />} />
                      <Route path="media" element={<AdminMedia />} />
                      <Route path="integrations" element={<AdminIntegrations />} />
                      <Route path="settings" element={<AdminSettings />} />
                    </Route>
                  </Route>
                </Routes>
              </CartProvider>
            </CompareProvider>
          </WishlistProvider>
            </SiteSettingsProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
